'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import {
  ArrowLeft,
  Award,
  BriefcaseBusiness,
  ExternalLink,
  FolderKanban,
  Loader2,
  Mail,
  MapPin,
  RefreshCw,
  ShieldCheck,
} from 'lucide-react';
import { useTranslation } from '@/i18n/useTranslation';
import AppImage from '@/components/ui/AppImage';

type CareerRecord = {
  id: string;
  title: string;
  organization: string | null;
  start_date: string;
  end_date: string | null;
  is_current: boolean;
  location: string | null;
  description: string | null;
  institution_confirmation_record_id: string | null;
};

export type PublicTrustState =
  | 'institution_confirmed'
  | 'witnessed'
  | 'evidence_attached'
  | 'verified_record'
  | 'self_recorded';

export function professionalTrustLabel(state: PublicTrustState) {
  const labels: Record<PublicTrustState, { key: string; fallback: string }> = {
    institution_confirmed: {
      key: 'life_record.institution_confirmed',
      fallback: 'Institution-confirmed',
    },
    witnessed: { key: 'life_record.witnessed', fallback: 'Witnessed' },
    evidence_attached: {
      key: 'life_record.evidence_attached',
      fallback: 'Evidence attached',
    },
    verified_record: {
      key: 'life_record.verified_record',
      fallback: 'Verified record',
    },
    self_recorded: { key: 'life_record.self_recorded', fallback: 'Self-recorded' },
  };
  return labels[state];
}

type Payload = {
  profile: {
    full_name: string;
    public_handle: string;
    bio: string | null;
    avatar_url: string | null;
    banner_url: string | null;
    location: string | null;
  };
  settings: {
    headline: string | null;
    contact_email: string | null;
    visible_sections: string[];
  };
  currentRole: CareerRecord | null;
  career: CareerRecord[];
  projects: Array<{
    id: string;
    title: string;
    project_type: string;
    role_label: string | null;
    summary: string | null;
    start_date: string | null;
    end_date: string | null;
    status: string;
    external_url: string | null;
  }>;
  milestones: Array<{
    id: string;
    project_id: string;
    title: string;
    milestone_date: string | null;
    outcome: string | null;
    position: number;
  }>;
  achievements: Array<{
    id: string;
    title: string;
    institution: string | null;
    achievement_date: string | null;
    description: string | null;
    status: string;
    trust_state: PublicTrustState;
  }>;
  credentials: Array<{
    id: string;
    title: string;
    issuer_name: string;
    issue_date: string | null;
    credential_type: string;
    verification_ref: string | null;
    trust_level: string;
    trust_state: PublicTrustState;
  }>;
};

type LoadState = 'loading' | 'ready' | 'unavailable' | 'network';

function year(value: string | null) {
  return value?.slice(0, 4) || '';
}

function period(start: string | null, end: string | null, current: boolean, present: string) {
  return [year(start), current ? present : year(end)].filter(Boolean).join(' – ');
}

function titleCase(value: string) {
  return value.replaceAll('_', ' ').replace(/\b\w/g, (character) => character.toUpperCase());
}

export default function ProfessionalPublicView({ handle }: { handle: string }) {
  const { safeT } = useTranslation();
  const [data, setData] = useState<Payload | null>(null);
  const [state, setState] = useState<LoadState>('loading');
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setState('loading');
    fetch(`/api/public/professional?handle=${encodeURIComponent(handle)}`, {
      cache: 'no-store',
      signal: controller.signal,
    })
      .then(async (response) => {
        if (response.status === 404) {
          setState('unavailable');
          return null;
        }
        if (!response.ok) {
          setState('network');
          return null;
        }
        return (await response.json()) as Payload;
      })
      .then((payload) => {
        if (!payload) return;
        setData(payload);
        setState('ready');
      })
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === 'AbortError') return;
        setState('network');
      });
    return () => controller.abort();
  }, [attempt, handle]);

  if (state === 'loading') {
    return (
      <main className="flex min-h-screen items-center justify-center bg-[#f7faf9] text-[#163f45] dark:bg-[#101722] dark:text-[#9fd8d2]">
        <Loader2
          className="animate-spin"
          size={28}
          aria-label={safeT('common.loading' as never, 'Loading')}
        />
      </main>
    );
  }

  if (state !== 'ready' || !data) {
    const network = state === 'network';
    return (
      <main className="flex min-h-screen items-center justify-center bg-[#f7faf9] px-5 text-[#182433] dark:bg-[#101722] dark:text-[#edf4f3]">
        <div className="max-w-md text-center">
          {network ? (
            <RefreshCw className="mx-auto text-[#5f7377] dark:text-[#91a7aa]" size={28} />
          ) : (
            <ShieldCheck className="mx-auto text-[#5f7377] dark:text-[#91a7aa]" size={28} />
          )}
          <h1 className="mt-4 text-2xl font-semibold tracking-tight">
            {network
              ? safeT(
                  'life_record.professional_network_error' as never,
                  'Professional View could not load'
                )
              : safeT(
                  'life_record.professional_unavailable' as never,
                  'Professional View is private'
                )}
          </h1>
          <p className="mt-2 text-sm leading-6 text-[#5f6d77] dark:text-[#a7b6bb]">
            {network
              ? safeT(
                  'life_record.network_error_body' as never,
                  'Check your connection and try again.'
                )
              : safeT(
                  'life_record.professional_unavailable_body' as never,
                  'This person has not published a selective Professional View.'
                )}
          </p>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-4">
            {network && (
              <button
                type="button"
                onClick={() => setAttempt((value) => value + 1)}
                className="inline-flex min-h-11 items-center gap-2 rounded-full bg-[#153f47] px-5 text-sm font-semibold text-white transition-colors duration-150 hover:bg-[#0f343b] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1d7774] dark:bg-[#9fd8d2] dark:text-[#102127] dark:hover:bg-[#b7e6e1]"
              >
                <RefreshCw size={15} />
                {safeT('common.retry' as never, 'Retry')}
              </button>
            )}
            <Link
              href={`/u/${handle}`}
              className="inline-flex min-h-11 items-center gap-2 rounded-full px-3 text-sm font-semibold text-[#176763] transition-colors duration-150 hover:text-[#0e4d4a] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1d7774] dark:text-[#9fd8d2]"
            >
              <ArrowLeft size={15} />
              {safeT('life_record.back_to_profile' as never, 'Back to public profile')}
            </Link>
          </div>
        </div>
      </main>
    );
  }

  const {
    profile,
    settings,
    currentRole,
    career,
    projects,
    milestones,
    achievements,
    credentials,
  } = data;
  const present = safeT('life_record.present' as never, 'Present');
  const contentCount = career.length + projects.length + achievements.length + credentials.length;

  return (
    <main className="min-h-screen bg-[#f7faf9] text-[#182433] dark:bg-[#101722] dark:text-[#edf4f3]">
      <div className="border-b border-[#dbe5e2] bg-white/75 backdrop-blur dark:border-white/10 dark:bg-[#141e2b]/90">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-4 px-5 py-4">
          <Link
            href={`/u/${handle}`}
            className="inline-flex min-h-11 items-center gap-2 text-xs font-semibold text-[#5f6d77] transition-colors duration-150 hover:text-[#176763] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1d7774] dark:text-[#a7b6bb] dark:hover:text-[#9fd8d2]"
          >
            <ArrowLeft size={14} />
            {safeT('life_record.full_living_record' as never, 'Full living record')}
          </Link>
          <span className="text-right text-[10px] font-bold uppercase tracking-[.18em] text-[#176763] dark:text-[#9fd8d2]">
            Ertzyx · {safeT('life_record.professional' as never, 'Professional View')}
          </span>
        </div>
      </div>

      <header className="border-b border-[#dbe5e2] bg-white/70 dark:border-white/10 dark:bg-[#141e2b]">
        <div className="mx-auto grid max-w-5xl gap-7 px-5 py-10 sm:grid-cols-[88px_1fr] sm:py-14">
          {profile.avatar_url ? (
            <AppImage
              src={profile.avatar_url}
              alt=""
              width={80}
              height={80}
              priority
              className="h-20 w-20 rounded-full object-cover ring-1 ring-[#cddbd7] dark:ring-white/15"
            />
          ) : (
            <div className="flex h-20 w-20 items-center justify-center rounded-full bg-[#153f47] text-2xl font-semibold text-white dark:bg-[#9fd8d2] dark:text-[#102127]">
              {profile.full_name.slice(0, 1)}
            </div>
          )}
          <div>
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-4xl font-semibold tracking-[-0.035em] sm:text-5xl">
                {profile.full_name}
              </h1>
              <span className="inline-flex items-center gap-1 rounded-full border border-[#b9d4cf] bg-[#eef8f5] px-2.5 py-1 text-[10px] font-bold uppercase tracking-[.1em] text-[#176763] dark:border-[#4e6f70] dark:bg-[#193036] dark:text-[#a9ddd7]">
                <ShieldCheck size={12} />
                {safeT('life_record.owner_curated' as never, 'Owner-curated')}
              </span>
            </div>
            {(settings.headline || currentRole) && (
              <p className="mt-3 text-lg text-[#44545f] dark:text-[#c0ced1]">
                {settings.headline ||
                  (currentRole
                    ? [currentRole.title, currentRole.organization].filter(Boolean).join(' · ')
                    : '')}
              </p>
            )}
            {profile.bio && (
              <p className="mt-4 max-w-2xl text-sm leading-6 text-[#5f6d77] dark:text-[#a7b6bb]">
                {profile.bio}
              </p>
            )}
            <div className="mt-4 flex flex-wrap gap-4 text-xs text-[#5f6d77] dark:text-[#9eafb4]">
              {profile.location && (
                <span className="inline-flex items-center gap-1.5">
                  <MapPin size={13} />
                  {profile.location}
                </span>
              )}
              {settings.contact_email && (
                <a
                  className="inline-flex min-h-8 items-center gap-1.5 font-semibold text-[#176763] hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1d7774] dark:text-[#9fd8d2]"
                  href={`mailto:${settings.contact_email}`}
                >
                  <Mail size={13} />
                  {safeT('life_record.contact' as never, 'Contact')}
                </a>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-5xl space-y-14 px-5 py-10 sm:py-14">
        {currentRole && (
          <EditorialSection
            icon={BriefcaseBusiness}
            title={safeT('life_record.current_role' as never, 'Current role')}
          >
            <CareerRow entry={currentRole} present={present} safeT={safeT} compact />
          </EditorialSection>
        )}

        {career.length > 0 && (
          <EditorialSection
            icon={BriefcaseBusiness}
            title={safeT('life_record.experience' as never, 'Experience')}
          >
            {career.map((entry) => (
              <CareerRow key={entry.id} entry={entry} present={present} safeT={safeT} />
            ))}
          </EditorialSection>
        )}

        {projects.length > 0 && (
          <EditorialSection
            icon={FolderKanban}
            title={safeT('life_record.projects' as never, 'Projects & Works')}
          >
            <div className="divide-y divide-[#dbe5e2] border-y border-[#dbe5e2] dark:divide-white/10 dark:border-white/10">
              {projects.map((project) => {
                const projectMilestones = milestones
                  .filter((item) => item.project_id === project.id)
                  .slice(0, 4);
                return (
                  <article key={project.id} className="grid gap-3 py-6 sm:grid-cols-[150px_1fr]">
                    <div>
                      <p className="text-xs font-semibold text-[#5f6d77] dark:text-[#9eafb4]">
                        {period(
                          project.start_date,
                          project.end_date,
                          project.status === 'ongoing',
                          present
                        )}
                      </p>
                      <p className="mt-2 text-[10px] font-bold uppercase tracking-[.13em] text-[#176763] dark:text-[#9fd8d2]">
                        {safeT(
                          `life_record.project_type_${project.project_type}` as never,
                          titleCase(project.project_type)
                        )}
                      </p>
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold tracking-tight">{project.title}</h3>
                      <p className="mt-1 text-xs text-[#5f6d77] dark:text-[#9eafb4]">
                        {[
                          project.role_label,
                          safeT(
                            `life_record.project_status_${project.status}` as never,
                            titleCase(project.status)
                          ),
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </p>
                      {project.summary && (
                        <p className="mt-3 max-w-2xl text-sm leading-6 text-[#44545f] dark:text-[#bdc9cc]">
                          {project.summary}
                        </p>
                      )}
                      {projectMilestones.length > 0 && (
                        <div className="mt-4 border-l-2 border-[#c8dad5] pl-3 dark:border-[#456266]">
                          <p className="text-[10px] font-bold uppercase tracking-[.12em] text-[#5f6d77] dark:text-[#9eafb4]">
                            {safeT(
                              'life_record.selected_milestones' as never,
                              'Selected milestones'
                            )}
                          </p>
                          <ul className="mt-2 space-y-1.5 text-xs text-[#44545f] dark:text-[#bdc9cc]">
                            {projectMilestones.map((item) => (
                              <li key={item.id}>• {item.title}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      <div className="mt-4 flex flex-wrap items-center gap-4">
                        <TrustLabel
                          label={safeT('life_record.self_recorded' as never, 'Self-recorded')}
                        />
                        {project.external_url && (
                          <a
                            href={project.external_url}
                            rel="noreferrer"
                            target="_blank"
                            className="inline-flex min-h-8 items-center gap-1 text-xs font-bold text-[#176763] hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1d7774] dark:text-[#9fd8d2]"
                          >
                            {safeT('life_record.visit_work' as never, 'Visit work')}
                            <ExternalLink size={12} />
                          </a>
                        )}
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </EditorialSection>
        )}

        {achievements.length > 0 && (
          <EditorialSection
            icon={Award}
            title={safeT('life_record.achievements' as never, 'Achievements')}
          >
            <div className="divide-y divide-[#dbe5e2] border-y border-[#dbe5e2] dark:divide-white/10 dark:border-white/10">
              {achievements.map((item) => (
                <article key={item.id} className="grid gap-2 py-5 sm:grid-cols-[150px_1fr]">
                  <p className="text-xs font-semibold text-[#5f6d77] dark:text-[#9eafb4]">
                    {year(item.achievement_date)}
                  </p>
                  <div>
                    <h3 className="text-base font-semibold">{item.title}</h3>
                    {item.institution && (
                      <p className="mt-1 text-sm text-[#5f6d77] dark:text-[#a7b6bb]">
                        {item.institution}
                      </p>
                    )}
                    {item.description && (
                      <p className="mt-2 max-w-2xl text-sm leading-6 text-[#44545f] dark:text-[#bdc9cc]">
                        {item.description}
                      </p>
                    )}
                    <div className="mt-3">
                      <TrustLabel label={translateTrustState(item.trust_state, safeT)} />
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </EditorialSection>
        )}

        {credentials.length > 0 && (
          <EditorialSection
            icon={ShieldCheck}
            title={safeT('life_record.credentials' as never, 'Credentials & Verified Records')}
          >
            <div className="divide-y divide-[#dbe5e2] border-y border-[#dbe5e2] dark:divide-white/10 dark:border-white/10">
              {credentials.map((item) => (
                <article key={item.id} className="grid gap-2 py-5 sm:grid-cols-[150px_1fr]">
                  <p className="text-xs font-semibold text-[#5f6d77] dark:text-[#9eafb4]">
                    {year(item.issue_date)}
                  </p>
                  <div>
                    <h3 className="text-base font-semibold">{item.title}</h3>
                    <p className="mt-1 text-sm text-[#5f6d77] dark:text-[#a7b6bb]">
                      {item.issuer_name}
                    </p>
                    <div className="mt-3 flex flex-wrap items-center gap-4">
                      <TrustLabel label={translateTrustState(item.trust_state, safeT)} />
                      {item.verification_ref && (
                        <Link
                          href={`/credential/${encodeURIComponent(item.verification_ref)}`}
                          className="inline-flex min-h-8 items-center gap-1 text-xs font-bold text-[#176763] hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1d7774] dark:text-[#9fd8d2]"
                        >
                          {safeT('life_record.view_trust_record' as never, 'View trust record')}
                          <ExternalLink size={12} />
                        </Link>
                      )}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </EditorialSection>
        )}

        {contentCount === 0 && !currentRole && (
          <p className="py-16 text-center text-sm text-[#5f6d77] dark:text-[#a7b6bb]">
            {safeT(
              'life_record.no_professional_records' as never,
              'No records have been selected for this view yet.'
            )}
          </p>
        )}
      </div>
    </main>
  );
}

function CareerRow({
  entry,
  present,
  safeT,
  compact = false,
}: {
  entry: CareerRecord;
  present: string;
  safeT: (key: never, fallback: string) => string;
  compact?: boolean;
}) {
  return (
    <article className="grid gap-2 border-t border-[#dbe5e2] py-6 sm:grid-cols-[150px_1fr] dark:border-white/10">
      <p className="text-xs font-semibold text-[#5f6d77] dark:text-[#9eafb4]">
        {period(entry.start_date, entry.end_date, entry.is_current, present)}
      </p>
      <div>
        <h3 className="text-lg font-semibold">{entry.title}</h3>
        {entry.organization && (
          <p className="mt-0.5 text-sm text-[#5f6d77] dark:text-[#a7b6bb]">
            {entry.organization}
            {entry.location ? ` · ${entry.location}` : ''}
          </p>
        )}
        {entry.description && !compact && (
          <p className="mt-3 max-w-2xl text-sm leading-6 text-[#44545f] dark:text-[#bdc9cc]">
            {entry.description}
          </p>
        )}
        <div className="mt-3">
          <TrustLabel
            label={
              entry.institution_confirmation_record_id
                ? safeT('life_record.institution_confirmed' as never, 'Institution-confirmed')
                : safeT('life_record.self_recorded' as never, 'Self-recorded')
            }
          />
        </div>
      </div>
    </article>
  );
}

function TrustLabel({ label }: { label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-[#c8dad5] bg-[#f2f8f6] px-2.5 py-1 text-[10px] font-semibold text-[#4f6467] dark:border-[#456266] dark:bg-[#182931] dark:text-[#b4c9c9]">
      <ShieldCheck size={11} aria-hidden />
      {label}
    </span>
  );
}

function translateTrustState(
  state: PublicTrustState,
  safeT: ReturnType<typeof useTranslation>['safeT']
) {
  const label = professionalTrustLabel(state);
  return safeT(label.key as never, label.fallback);
}

function EditorialSection({
  icon: Icon,
  title,
  children,
}: {
  icon: typeof Award;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section>
      <div className="mb-4 flex items-center gap-2">
        <Icon size={17} className="text-[#176763] dark:text-[#9fd8d2]" />
        <h2 className="text-2xl font-semibold tracking-tight">{title}</h2>
      </div>
      {children}
    </section>
  );
}
